import { useTranslation } from 'react-i18next';
import React, { useState, useMemo } from 'react';
import { ChevronRight, CheckCircle, Database, Truck, Calendar, Package, ScanLine, Loader2, AlertTriangle, Edit3 } from 'lucide-react';
import VehicleCatalogSelector from '../../../common/VehicleCatalogSelector';
import { DocumentScanner, SCAN_CONFIGS } from '../../../document-scanner/index.js';

// ────────────────────────────────────────────────────────────────────────────
// OCR validation: range checks, format checks, energy code normalization.
// Returns { level: 'error' | 'warning' | null, message: string | null }.
// ────────────────────────────────────────────────────────────────────────────

// OCR energy codes (official carte grise field P.3) — codes themselves are
// regulatory, never translated; only the human-readable label is.
const ENERGY_CODE_KEYS = ['GO', 'ES', 'EL', 'EH', 'GH', 'GP', 'GN', 'FE'];

const ENERGY_ALIASES = {
  GO: 'GO', GAZOLE: 'GO', DIESEL: 'GO', 'GAS-OIL': 'GO',
  ES: 'ES', ESSENCE: 'ES', GASOLINE: 'ES', 'SANS PLOMB': 'ES',
  EL: 'EL', ELECTRIQUE: 'EL', ÉLECTRIQUE: 'EL', ELECTRIC: 'EL',
  EH: 'EH', HEV: 'EH', 'HYBRIDE ESSENCE': 'EH', HYBRIDE: 'EH',
  GH: 'GH', 'HYBRIDE GAZOLE': 'GH', 'HYBRIDE DIESEL': 'GH',
  GP: 'GP', GPL: 'GP',
  GN: 'GN', GNV: 'GN',
  FE: 'FE', SUPERETHANOL: 'FE', 'E85': 'FE',
};

function normalizeEnergy(raw) {
  if (!raw) return null;
  const key = String(raw).trim().toUpperCase();
  return ENERGY_ALIASES[key] || null;
}

function validateField(key, value, t) {
  if (!value || value === '???') return { level: null, message: null };
  const v = String(value).trim();
  const ns = 'vehicles.wizard.identification.ocr.validation';

  switch (key) {
    case 'immatriculation': {
      const newFmt = /^[A-Z]{2}-?\d{3}-?[A-Z]{2}$/i.test(v);
      const oldFmt = /^\d{1,4}\s*[A-Z]{1,3}\s*\d{2,3}$/i.test(v);
      if (!newFmt && !oldFmt) return { level: 'warning', message: t(`${ns}.plateFormat`) };
      return { level: null, message: null };
    }
    case 'vin': {
      if (!/^[A-HJ-NPR-Z0-9]{17}$/i.test(v)) {
        return { level: 'warning', message: t(`${ns}.vinInvalid`, { length: v.length }) };
      }
      return { level: null, message: null };
    }
    case 'co2': {
      const n = parseInt(v, 10);
      if (isNaN(n)) return { level: 'error', message: t(`${ns}.mustBeInteger`) };
      if (n < 0 || n > 400) return { level: 'warning', message: t(`${ns}.co2OutOfRange`, { value: n }) };
      return { level: null, message: null };
    }
    case 'masse_service': {
      const n = parseInt(v, 10);
      if (isNaN(n)) return { level: 'error', message: t(`${ns}.mustBeInteger`) };
      if (n <= 0) return { level: 'error', message: t(`${ns}.massMustBePositive`) };
      if (n < 400 || n > 40000) return { level: 'warning', message: t(`${ns}.massOutOfRange`, { value: n }) };
      return { level: null, message: null };
    }
    case 'puissance_fiscale': {
      const n = parseInt(v, 10);
      if (isNaN(n)) return { level: 'error', message: t(`${ns}.mustBeInteger`) };
      if (n < 1 || n > 99) return { level: 'warning', message: t(`${ns}.powerOutOfRange`, { value: n }) };
      return { level: null, message: null };
    }
    case 'energie': {
      const code = normalizeEnergy(v);
      if (!code) return { level: 'warning', message: t(`${ns}.unknownEnergyCode`, { value: v, codes: ENERGY_CODE_KEYS.join(', ') }) };
      const codeLabel = t(`vehicles.wizard.identification.ocr.energyCodes.${code}`);
      if (code !== v.toUpperCase()) {
        return { level: 'warning', message: t(`${ns}.willNormalize`, { code, label: codeLabel }) };
      }
      return { level: null, message: codeLabel };
    }
    case 'date_immat': {
      const m = v.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})$/);
      if (!m) return { level: 'warning', message: t(`${ns}.dateFormat`) };
      const [, d, mo, y] = m;
      const year = y.length === 2 ? 2000 + parseInt(y, 10) : parseInt(y, 10);
      const date = new Date(year, parseInt(mo, 10) - 1, parseInt(d, 10));
      const now = new Date();
      if (date > now) return { level: 'warning', message: t(`${ns}.dateFuture`) };
      if (year < 1900) return { level: 'warning', message: t(`${ns}.dateUnlikely`) };
      return { level: null, message: null };
    }
    default:
      return { level: null, message: null };
  }
}

/**
 * ScanReviewPanel - Shows OCR results for user to verify/correct before applying
 */
function ScanReviewPanel({ scanResult, onConfirm, onRescan, onCancel }) {
  const { t } = useTranslation();
  const { data, file } = scanResult;
  const [reviewed, setReviewed] = useState({
    immatriculation: data.immatriculation || '',
    vin: data.vin || '',
    marque: data.marque || '',
    denomination: data.denomination || '',
    date_immat: data.date_immat || '',
    titulaire: data.titulaire || '',
    genre: data.genre || '',
    puissance_fiscale: data.puissance_fiscale || '',
    energie: data.energie || '',
    co2: data.co2 || '',
    masse_service: data.masse_service || '',
  });

  const updateField = (field, value) => {
    setReviewed(prev => ({ ...prev, [field]: value }));
  };

  const hasUnknown = Object.values(reviewed).some(v => v === '???' || v === '');

  // Live validation map (recomputes on every keystroke)
  const validations = useMemo(() => {
    const out = {};
    for (const [key, value] of Object.entries(reviewed)) {
      out[key] = validateField(key, value, t);
    }
    return out;
  }, [reviewed, t]);

  const errorCount = Object.values(validations).filter(v => v.level === 'error').length;
  const warningCount = Object.values(validations).filter(v => v.level === 'warning').length;

  const handleConfirm = () => {
    // Normalize energy code at confirm time so downstream gets the canonical value.
    const out = { ...reviewed };
    const normalized = normalizeEnergy(out.energie);
    if (normalized) out.energie = normalized;
    onConfirm({ data: out, file });
  };

  const ocrf = 'vehicles.wizard.identification.ocr.fields';
  const fields = [
    { key: 'immatriculation', label: `A — ${t(`${ocrf}.immatriculation`)}`, placeholder: 'AA-123-BB' },
    { key: 'vin', label: `E — ${t(`${ocrf}.vin`)}`, placeholder: 'VF1RABCFG12345678' },
    { key: 'marque', label: `D.1 — ${t(`${ocrf}.marque`)}`, placeholder: 'CITROEN' },
    { key: 'denomination', label: `D.3 — ${t(`${ocrf}.denomination`)}`, placeholder: 'JUMPY' },
    { key: 'date_immat', label: `B — ${t(`${ocrf}.dateImmat`)}`, placeholder: 'JJ/MM/AAAA' },
    { key: 'titulaire', label: `C.1 — ${t(`${ocrf}.titulaire`)}`, placeholder: t(`${ocrf}.titulairePlaceholder`) },
    { key: 'genre', label: `J.1 — ${t(`${ocrf}.genre`)}`, placeholder: 'VP' },
    { key: 'puissance_fiscale', label: `P.6 — ${t(`${ocrf}.puissanceFiscale`)}`, placeholder: '7' },
    { key: 'energie', label: `P.3 — ${t(`${ocrf}.energie`)}`, placeholder: 'GO, ES, EL, EH...' },
    { key: 'co2', label: `V.7 — ${t(`${ocrf}.co2`)}`, placeholder: '149' },
    { key: 'masse_service', label: `G — ${t(`${ocrf}.masseService`)}`, placeholder: '1500' },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-4 rounded-t-lg">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Edit3 className="w-5 h-5" />
            {t('vehicles.wizard.identification.ocr.title')}
          </h3>
          <p className="text-sm text-amber-100 mt-1">
            {t('vehicles.wizard.identification.ocr.hint')}
          </p>
        </div>

        {/* Warning if unknowns */}
        {hasUnknown && (
          <div className="mx-6 mt-4 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              {t('vehicles.wizard.identification.ocr.unknownWarning')}
            </p>
          </div>
        )}

        {/* Validation summary */}
        {(errorCount > 0 || warningCount > 0) && (
          <div className={`mx-6 mt-3 flex items-start gap-2 p-3 rounded-lg border ${
            errorCount > 0 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
          }`}>
            <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${errorCount > 0 ? 'text-red-600' : 'text-amber-600'}`} />
            <p className={`text-sm ${errorCount > 0 ? 'text-red-800' : 'text-amber-800'}`}>
              {errorCount > 0 && <strong>{t('vehicles.wizard.identification.ocr.errorCount', { count: errorCount })}</strong>}
              {errorCount > 0 && warningCount > 0 && ' · '}
              {warningCount > 0 && <span>{t('vehicles.wizard.identification.ocr.warningCount', { count: warningCount })}</span>}
              {' '}{t('vehicles.wizard.identification.ocr.reviewBeforeContinue')}
            </p>
          </div>
        )}

        {/* Fields grid */}
        <div className="px-6 py-4 space-y-3">
          {fields.map(f => {
            const v = validations[f.key] || { level: null, message: null };
            const isEmpty = reviewed[f.key] === '???' || reviewed[f.key] === '';
            const borderClass = v.level === 'error'
              ? 'border-red-400 bg-red-50'
              : v.level === 'warning'
                ? 'border-amber-400 bg-amber-50'
                : isEmpty
                  ? 'border-amber-400 bg-amber-50'
                  : 'border-gray-300';
            return (
              <div key={f.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
                <input
                  type="text"
                  value={reviewed[f.key]}
                  onChange={(e) => updateField(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent ${borderClass}`}
                />
                {v.message && (
                  <p className={`text-xs mt-1 flex items-start gap-1 ${
                    v.level === 'error' ? 'text-red-600' : v.level === 'warning' ? 'text-amber-600' : 'text-gray-500'
                  }`}>
                    {v.level && <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />}
                    <span>{v.message}</span>
                  </p>
                )}
              </div>
            );
          })}
          {file && (
            <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 p-2 rounded">
              <ScanLine className="w-4 h-4" />
              {t('vehicles.wizard.identification.ocr.fileLabel', { name: file.name, size: (file.size / 1024).toFixed(0) })}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t bg-gray-50 rounded-b-lg flex justify-between">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg"
            >
              {t('vehicles.wizard.identification.ocr.cancel')}
            </button>
            <button
              type="button"
              onClick={onRescan}
              className="px-4 py-2 text-sm text-amber-700 hover:text-amber-800 border border-amber-300 rounded-lg bg-amber-50"
            >
              {t('vehicles.wizard.identification.ocr.rescan')}
            </button>
          </div>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={errorCount > 0}
            className={`px-6 py-2 text-sm font-semibold text-white rounded-lg flex items-center gap-2 ${
              errorCount > 0
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700'
            }`}
            title={errorCount > 0 ? t('vehicles.wizard.identification.ocr.confirmErrorsTooltip') : ''}
          >
            <CheckCircle className="w-4 h-4" />
            {t('vehicles.wizard.identification.ocr.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * StepIdentification - Step 1 of VehicleLifecycleWorkflow
 *
 * Captures core vehicle identification data:
 * - Optional: Select from vehicle catalog (auto-fills make, model, version, year, fuel type)
 * - Make (brand), Model, Version, Year
 * - License plate, VIN
 * - Fuel type
 */
function StepIdentification({ formData, updateFormData, showCatalogSelector, setShowCatalogSelector, handleCatalogSelect, showScanner, setShowScanner, onScanComplete, catalogVersions = [], loadingVersions = false }) {
  const { t } = useTranslation();
  const [freeTextVersion, setFreeTextVersion] = useState(false);
  const [scanReview, setScanReview] = useState(null);

  // Intercept scan completion — show review first
  const handleScanRawComplete = (result) => {
    setScanReview(result);
  };

  // User confirmed reviewed data
  const handleScanConfirm = (reviewedResult) => {
    setScanReview(null);
    onScanComplete(reviewedResult);
  };

  // User wants to re-scan
  const handleRescan = () => {
    setScanReview(null);
    // Scanner is still open (showScanner=true), just clear review
  };

  // User cancels review
  const handleReviewCancel = () => {
    setScanReview(null);
    setShowScanner(false);
  };

  // Scan review overlay
  if (scanReview) {
    return (
      <ScanReviewPanel
        scanResult={scanReview}
        onConfirm={handleScanConfirm}
        onRescan={handleRescan}
        onCancel={handleReviewCancel}
      />
    );
  }

  // Full-screen scanner overlay
  if (showScanner) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full h-[90vh] max-w-7xl overflow-hidden">
          <DocumentScanner
            config={SCAN_CONFIGS.carte_grise}
            onComplete={handleScanRawComplete}
            onCancel={() => setShowScanner(false)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-form="vehicle-basic">
      <h3 className="text-xl font-bold text-gray-900 mb-4">{t('vehicles.wizard.identification.title')}</h3>

      {/* Vehicle Catalog Selector Section */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setShowCatalogSelector(!showCatalogSelector)}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-amber-100/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Database className="w-5 h-5 text-amber-600" />
            <div>
              <span className="font-medium text-gray-900">{t('vehicles.wizard.identification.catalogTitle')}</span>
              <p className="text-sm text-gray-600">{t('vehicles.wizard.identification.catalogHint')}</p>
            </div>
          </div>
          <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${showCatalogSelector ? 'rotate-90' : ''}`} />
        </button>

        {showCatalogSelector && (
          <div className="p-4 border-t border-amber-200 bg-white">
            <VehicleCatalogSelector
              onSelect={handleCatalogSelect}
              placeholder={t('vehicles.wizard.identification.catalogPlaceholder')}
            />
            {formData.catalog_id && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 text-green-800">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {t('vehicles.wizard.identification.catalogSelected', { make: formData.make, model: formData.model, version: formData.version, year: formData.year })}
                  </span>
                </div>
                {formData.catalog_fiscal_power && (
                  <p className="text-xs text-green-600 mt-1 ml-6">
                    {t('vehicles.wizard.identification.catalogSpecs', { fiscalPower: formData.catalog_fiscal_power, powerHp: formData.catalog_power_hp, co2: formData.catalog_co2 })}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Carte Grise Scanner */}
      {setShowScanner && (
        <button
          type="button"
          onClick={() => setShowScanner(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-semibold transition-colors shadow-lg"
        >
          <ScanLine className="w-5 h-5" />
          {t('vehicles.wizard.identification.scanRegistration')}
        </button>
      )}

      {/* Manual Input or Adjust Values */}
      <div className="flex items-center gap-3 text-sm text-gray-500">
        <div className="flex-1 h-px bg-gray-200" />
        <span>{t('vehicles.wizard.identification.orManual')}</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.identification.make')}</label>
          <input
            type="text"
            value={formData.make}
            onChange={(e) => updateFormData('make', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            placeholder={t('vehicles.wizard.identification.makePlaceholder')}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.identification.model')}</label>
          <input
            type="text"
            value={formData.model}
            onChange={(e) => updateFormData('model', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            placeholder={t('vehicles.wizard.identification.modelPlaceholder')}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.identification.version')}</label>
          {loadingVersions ? (
            <div className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('vehicles.wizard.identification.loadingVersions')}
            </div>
          ) : catalogVersions.length > 0 && !freeTextVersion ? (
            <>
              <select
                value={formData.version}
                onChange={(e) => {
                  if (e.target.value === '__free_text__') {
                    setFreeTextVersion(true);
                  } else {
                    const selected = catalogVersions.find(v => v.version_name === e.target.value);
                    updateFormData('version', e.target.value);
                    if (selected) {
                      // Auto-fill catalog data from selected version
                      if (selected.fiscal_power) updateFormData('catalog_fiscal_power', selected.fiscal_power);
                      if (selected.power_hp) updateFormData('catalog_power_hp', selected.power_hp);
                      if (selected.co2_emission_wltp) updateFormData('catalog_co2', selected.co2_emission_wltp);
                      if (selected.price_eur) updateFormData('catalog_price', selected.price_eur);
                      if (selected.id) updateFormData('catalog_id', selected.id);
                      if (selected.tank_capacity_l) updateFormData('tank_capacity_liters', selected.tank_capacity_l);
                    }
                  }
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              >
                <option value="">{t('vehicles.wizard.identification.selectVersion')}</option>
                {catalogVersions.map((v, i) => {
                  // Strip brand prefix from version_name if present (catalog data has "Citroen C5 X" as model_name)
                  const brand = (formData.make || '').toLowerCase();
                  const cleanName = brand && v.version_name.toLowerCase().startsWith(brand + ' ')
                    ? v.version_name.substring(brand.length + 1) : v.version_name;
                  return (
                    <option key={v.id || i} value={v.version_name}>
                      {cleanName}{v.fiscal_power ? ` (${v.fiscal_power} CV)` : ''}{v.motorization ? ` - ${v.motorization}` : ''}
                    </option>
                  );
                })}
                <option value="__free_text__">{t('vehicles.wizard.identification.otherVersion')}</option>
              </select>
              {catalogVersions.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">{t('vehicles.wizard.identification.versionsFound', { count: catalogVersions.length })}</p>
              )}
            </>
          ) : (
            <>
              <input
                type="text"
                value={formData.version}
                onChange={(e) => updateFormData('version', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder={t('vehicles.wizard.identification.versionPlaceholder')}
              />
              {catalogVersions.length > 0 && freeTextVersion && (
                <button
                  type="button"
                  onClick={() => setFreeTextVersion(false)}
                  className="text-xs text-amber-600 hover:text-amber-700 mt-1 underline"
                >
                  {t('vehicles.wizard.identification.backToList')}
                </button>
              )}
            </>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.identification.year')}</label>
          <input
            type="number"
            value={formData.year}
            onChange={(e) => updateFormData('year', parseInt(e.target.value))}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            min="1900"
            max={new Date().getFullYear() + 1}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.identification.plate')}</label>
          <input
            type="text"
            value={formData.license_plate}
            onChange={(e) => updateFormData('license_plate', e.target.value.toUpperCase())}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            placeholder={t('vehicles.wizard.identification.platePlaceholder')}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.identification.vin')}</label>
          <input
            type="text"
            value={formData.vin}
            onChange={(e) => updateFormData('vin', e.target.value.toUpperCase())}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            placeholder={t('vehicles.wizard.identification.vinPlaceholder')}
            maxLength="17"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.identification.fuelType')}</label>
          <select
            value={formData.fuel_type}
            onChange={(e) => updateFormData('fuel_type', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          >
            <option value="gasoline">{t('vehicles.fuelType.gasoline')}</option>
            <option value="diesel">{t('vehicles.fuelType.diesel')}</option>
            <option value="electric">{t('vehicles.fuelType.electric')}</option>
            <option value="hybrid">{t('vehicles.fuelType.hybrid')}</option>
          </select>
        </div>

        {/* Odometer moved to Step 7 (Etat initial) */}
      </div>

      {/* Reception Status Section */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-4">
        <h4 className="font-semibold text-amber-900 flex items-center gap-2">
          <Truck className="w-5 h-5" />
          {t('vehicles.wizard.identification.receptionTitle')}
        </h4>

        <div className="grid grid-cols-2 gap-4">
          <label
            className={`flex flex-col items-center p-4 rounded-lg border-2 cursor-pointer transition-colors ${
              formData.reception_status === 'received'
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 hover:border-amber-300'
            }`}
          >
            <input
              type="radio"
              name="reception_status"
              value="received"
              checked={formData.reception_status === 'received'}
              onChange={() => {
                updateFormData('reception_status', 'received');
                updateFormData('status', 'active');
              }}
              className="sr-only"
            />
            <CheckCircle className={`w-8 h-8 mb-2 ${
              formData.reception_status === 'received' ? 'text-green-600' : 'text-gray-400'
            }`} />
            <span className="font-medium text-gray-900">{t('vehicles.wizard.identification.received')}</span>
            <span className="text-xs text-gray-500 text-center mt-1">
              {t('vehicles.wizard.identification.receivedHint')}
            </span>
          </label>

          <label
            className={`flex flex-col items-center p-4 rounded-lg border-2 cursor-pointer transition-colors ${
              formData.reception_status === 'ordered'
                ? 'border-amber-500 bg-amber-50'
                : 'border-gray-200 hover:border-amber-300'
            }`}
          >
            <input
              type="radio"
              name="reception_status"
              value="ordered"
              checked={formData.reception_status === 'ordered'}
              onChange={() => {
                updateFormData('reception_status', 'ordered');
                updateFormData('status', 'ordered');
              }}
              className="sr-only"
            />
            <Package className={`w-8 h-8 mb-2 ${
              formData.reception_status === 'ordered' ? 'text-amber-600' : 'text-gray-400'
            }`} />
            <span className="font-medium text-gray-900">{t('vehicles.wizard.identification.ordered')}</span>
            <span className="text-xs text-gray-500 text-center mt-1">
              {t('vehicles.wizard.identification.orderedHint')}
            </span>
          </label>
        </div>

        {/* Dates based on status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          {formData.reception_status === 'received' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-green-600" />
                  {t('vehicles.wizard.identification.receptionDate')}
                </label>
                <input
                  type="date"
                  value={formData.entry_date}
                  onChange={(e) => updateFormData('entry_date', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">{t('vehicles.wizard.identification.receptionDateHint')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('vehicles.wizard.identification.serviceStartDate')}
                </label>
                <input
                  type="date"
                  value={formData.service_start_date || formData.entry_date}
                  onChange={(e) => updateFormData('service_start_date', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">{t('vehicles.wizard.identification.serviceStartDateHint')}</p>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-amber-600" />
                  {t('vehicles.wizard.identification.orderDate')}
                </label>
                <input
                  type="date"
                  value={formData.order_date || new Date().toISOString().split('T')[0]}
                  onChange={(e) => updateFormData('order_date', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Truck className="w-4 h-4 text-amber-600" />
                  {t('vehicles.wizard.identification.expectedDeliveryDate')}
                </label>
                <input
                  type="date"
                  value={formData.expected_delivery_date}
                  onChange={(e) => updateFormData('expected_delivery_date', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">{t('vehicles.wizard.identification.expectedDeliveryHint')}</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* License Requirements Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
        <h4 className="font-semibold text-blue-900">{t('vehicles.wizard.identification.qualificationsTitle')}</h4>
        <p className="text-sm text-blue-800">
          {t('vehicles.wizard.identification.qualificationsHint')}
        </p>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.identification.licenseCategory')}</label>
          <select
            value={formData.required_license_category}
            onChange={(e) => updateFormData('required_license_category', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="B">{t('vehicles.wizard.identification.licenseB')}</option>
            <option value="C">{t('vehicles.wizard.identification.licenseC')}</option>
            <option value="D">{t('vehicles.wizard.identification.licenseD')}</option>
            <option value="CE">{t('vehicles.wizard.identification.licenseCE')}</option>
          </select>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">{t('vehicles.wizard.identification.certificationsTitle')}</p>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.requires_cert_adr}
              onChange={(e) => updateFormData('requires_cert_adr', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <span className="text-sm text-gray-700">{t('vehicles.wizard.identification.certAdr')}</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.requires_cert_fimo}
              onChange={(e) => updateFormData('requires_cert_fimo', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <span className="text-sm text-gray-700">{t('vehicles.wizard.identification.certFimo')}</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.requires_cert_crane}
              onChange={(e) => updateFormData('requires_cert_crane', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <span className="text-sm text-gray-700">{t('vehicles.wizard.identification.certCrane')}</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.requires_cert_forklift}
              onChange={(e) => updateFormData('requires_cert_forklift', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <span className="text-sm text-gray-700">{t('vehicles.wizard.identification.certForklift')}</span>
          </label>
        </div>
      </div>
    </div>
  );
}

export default StepIdentification;
